import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SocialMediaIntegrationProps {
  facebookPageId?: string;
  facebookAppId?: string;
}

const PLACEHOLDER_FB_APP_ID = '1234567890123456';

const SocialMediaIntegration: React.FC<SocialMediaIntegrationProps> = ({
  facebookPageId = '61577953652127',
  facebookAppId = (import.meta.env.VITE_FACEBOOK_APP_ID as string) || PLACEHOLDER_FB_APP_ID
}) => {
  const location = useLocation();
  const hasRealAppId = !!import.meta.env.VITE_FACEBOOK_APP_ID && import.meta.env.VITE_FACEBOOK_APP_ID !== PLACEHOLDER_FB_APP_ID && facebookAppId !== PLACEHOLDER_FB_APP_ID;

  useEffect(() => {
    // Ladda Facebook SDK endast på Om oss OCH när en riktig App ID är satt (annars 400 + X-Frame-Options-fel).
    const needsFacebookPlugin = location.pathname === '/om-oss' && hasRealAppId;

    const loadFacebookSDK = () => {
      if (document.getElementById('facebook-jssdk')) {
        return;
      }

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://connect.facebook.net/sv_SE/sdk.js#xfbml=1&version=v18.0&appId=${facebookAppId}`;

      script.onerror = () => {
        console.warn('[SocialMediaIntegration] Facebook SDK kunde inte laddas (t.ex. ogiltig appId eller nätverksfel). Plugin visas inte.');
      };

      document.body.appendChild(script);

      window.fbAsyncInit = function () {
        if (typeof window.FB !== 'undefined') {
          window.FB.init({
            appId: facebookAppId,
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v18.0',
          });
        }
      };
    };

    if (needsFacebookPlugin) {
      loadFacebookSDK();
    }

    const parseFacebookElements = () => {
      if (window.FB) {
        try {
          window.FB.XFBML.parse();
        } catch {
          // Ignorera om SDK inte är redo
        }
      }
    };

    if (needsFacebookPlugin) {
      parseFacebookElements();
    }

    return () => {};
  }, [location.pathname, facebookAppId, hasRealAppId]);

  return null; // This component just loads the SDKs, doesn't render anything
};

// Add Facebook SDK type definition
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

export default SocialMediaIntegration;